import clsx from 'clsx'
import { useEffect } from 'react'
import { useWatch } from 'react-hook-form'

import { usePostByTenantAccountPricesAndAccountIdMutation } from '@/api/rtk-api'
import { Currency } from '@/components/core-v2'
import { TENANT } from '@/const'

import { TradeTransferUnits } from '../unit-transfers-utilities/map-data'
import classes from './unit-transfers-asset.module.scss'

interface UnitTransfersPriceRowProps {
  trade: TradeTransferUnits
}

const getIds = (trade: TradeTransferUnits) => {
  const prefixes = trade.transferUnits.map(transferUnit => `${trade.id}-${transferUnit.assetId}`)
  return {
    assetIds: trade.transferUnits.map(transferUnit => transferUnit.assetId),
    unitIds: prefixes.map(x => `${x}-unit`),
    priceIds: prefixes.map(x => `${x}-price`)
  }
}

const UnitTransfersPriceRow = ({ trade }: UnitTransfersPriceRowProps) => {
  const { assetIds, priceIds, unitIds } = getIds(trade)
  const unitValues = useWatch({ name: unitIds })
  const priceValues = useWatch({ name: priceIds })
  const [mutate, { data }] = usePostByTenantAccountPricesAndAccountIdMutation()

  useEffect(() => {
    mutate({
      accountId: trade.id,
      tenant: TENANT,
      body: assetIds.map((assetId, index) => ({
        assetId,
        originalPurchasePrice: priceValues[index]?.numerical
          ? priceValues[index].numerical / 100
          : null,
        units: unitValues[index]?.numerical
      }))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutate, ...unitValues, ...priceValues])

  return (
    <div className={clsx(classes.row)}>
      <div className={clsx(classes.rowValue)}>
        <Currency code="ZAR" value={data?.basePortfolioValue} />
      </div>
      <div className={clsx(classes.rowValue)}>
        <Currency code="ZAR" value={data?.currentPortfolioValue} />
      </div>
      <div className={clsx(classes.rowValue)}>
        {data?.noteUnits.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 6
        })}
      </div>
      <div className={clsx(classes.rowValue)}>
        <Currency code="ZAR" value={data?.baseCostPerUnit} />
      </div>
      <div className={clsx(classes.rowValue)}>
        <Currency code="ZAR" value={data?.notePrice} />
      </div>
    </div>
  )
}

interface UnitTransfersPricesProps {
  trades: Map<string, TradeTransferUnits>
}

export const UnitTransfersPrices = ({ trades }: UnitTransfersPricesProps) => (
  <div className={clsx(classes.root, classes.priceColumn)}>
    <div className={clsx(classes.root, classes.priceHeadings)}>
      <span>Base portfolio value</span>
      <span>Current portfolio value</span>
      <span>Note units</span>
      <span>Base cost per unit value</span>
      <span>Note price</span>
    </div>
    {[...trades.values()].map(trade => (
      <UnitTransfersPriceRow key={trade.id} trade={trade} />
    ))}
  </div>
)
